import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast';
import { Mail, Send, FileText } from 'lucide-react';

const EmailSettings: React.FC = () => {
  const [settings, setSettings] = useState({
    id: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    notification_schedule: 'daily',
    notification_time: '09:00',
    enabled: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const { data, error } = await supabase.from('email_settings').select('*').limit(1).single();
    if (data) setSettings(data);
    if (error) console.error(error);
  };

  const saveSettings = async () => {
    setLoading(true);
    const { error } = await supabase.from('email_settings').update(settings).eq('id', settings.id);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Email settings saved' });
    }
    setLoading(false);
  };

  const sendTestEmail = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke('send-test-email', {
      body: { settings }
    });
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Test email sent' });
    }
    setLoading(false);
  };

  const sendReportSummary = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke('send-report-summary');
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Report summaries sent to all users' });
    }
    setLoading(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Notification Settings
        </CardTitle>
        <CardDescription>Configure SMTP and notification schedule</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Enable Email Notifications</Label>
          <Switch checked={settings.enabled} onCheckedChange={(checked) => setSettings({...settings, enabled: checked})} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>SMTP Host</Label>
            <Input value={settings.smtp_host} onChange={(e) => setSettings({...settings, smtp_host: e.target.value})} placeholder="smtp.gmail.com" />
          </div>
          <div className="space-y-2">
            <Label>SMTP Port</Label>
            <Input type="number" value={settings.smtp_port} onChange={(e) => setSettings({...settings, smtp_port: parseInt(e.target.value)})} />
          </div>
        </div>
        <div className="space-y-2">
          <Label>SMTP Username</Label>
          <Input value={settings.smtp_user} onChange={(e) => setSettings({...settings, smtp_user: e.target.value})} />
        </div>
        <div className="space-y-2">
          <Label>SMTP Password</Label>
          <Input type="password" value={settings.smtp_password} onChange={(e) => setSettings({...settings, smtp_password: e.target.value})} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>From Email</Label>
            <Input value={settings.from_email} onChange={(e) => setSettings({...settings, from_email: e.target.value})} />
          </div>
          <div className="space-y-2">
            <Label>From Name</Label>
            <Input value={settings.from_name} onChange={(e) => setSettings({...settings, from_name: e.target.value})} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Notification Schedule</Label>
            <Select value={settings.notification_schedule} onValueChange={(value) => setSettings({...settings, notification_schedule: value})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Notification Time</Label>
            <Input type="time" value={settings.notification_time} onChange={(e) => setSettings({...settings, notification_time: e.target.value})} />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={saveSettings} disabled={loading}>Save Settings</Button>
          <Button variant="outline" onClick={sendTestEmail} disabled={loading}>
            <Send className="h-4 w-4 mr-2" />Test Email
          </Button>
          <Button variant="secondary" onClick={sendReportSummary} disabled={loading}>
            <FileText className="h-4 w-4 mr-2" />Send Report Summary Now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailSettings;
